import React, { useState, useMemo } from 'react';
import { X, Plus, Search, Check, Folder, ArrowRight } from 'lucide-react';
import { Prompt } from '../../types'; // Keep this import
import { usePrompts } from '../../hooks/usePrompts';
import { useFolders } from '../../hooks/useFolders';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useAuth } from '../../hooks/useAuth';
import { useScrollLock } from '../../hooks/useScrollLock';

interface AddPromptsToFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetFolderId: string;
  onMovePrompts: (promptIds: string[], folderId: string) => Promise<void>;
}

export const AddPromptsToFolderModal: React.FC<AddPromptsToFolderModalProps> = ({
  isOpen,
  onClose,
  targetFolderId,
  onMovePrompts,
}) => {
  const { user } = useAuth();
  const { allPrompts } = usePrompts(user?.id || null); // Get all prompts for the current user
  const { folders } = useFolders(user?.id || null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPromptIds, setSelectedPromptIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const modalRef = useClickOutside(() => {
    if (isOpen && !isLoading) {
      onClose();
    }
  });

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Get target folder details
  const targetFolder = folders.find(f => f.id === targetFolderId);

  // Filter prompts that are not already in the target folder
  const availablePrompts = useMemo(() => {
    const filtered = allPrompts.filter(prompt => {
      // Exclude prompts already in the target folder
      if (prompt.folderId === targetFolderId) return false;
      
      // Apply search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          prompt.title.toLowerCase().includes(query) ||
          prompt.body.toLowerCase().includes(query) ||
          prompt.domain?.toLowerCase().includes(query) ||
          prompt.aiPlatform?.toLowerCase().includes(query)
        );
      }
      
      return true;
    });

    // Sort by most recent first
    return filtered.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  }, [allPrompts, targetFolderId, searchQuery]);

  const handleTogglePrompt = (promptId: string) => {
    setSelectedPromptIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(promptId)) {
        newSet.delete(promptId);
      } else {
        newSet.add(promptId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedPromptIds(new Set(availablePrompts.map(p => p.id)));
  };

  const handleDeselectAll = () => {
    setSelectedPromptIds(new Set());
  };

  const handleAddPrompts = async () => {
    if (selectedPromptIds.size === 0) return;

    setIsLoading(true);
    try {
      await onMovePrompts(Array.from(selectedPromptIds), targetFolderId);
      onClose();
      setSelectedPromptIds(new Set());
      setSearchQuery('');
    } catch (error) {
      console.error('Error adding prompts to folder:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div ref={modalRef} className="bg-white dark:bg-dark-surface rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center">
            <Plus className="h-6 w-6 text-modular-frosted-indigo dark:text-modular-frosted-indigo mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text">
                Add Prompts to Folder
              </h2>
              {targetFolder && (
                <div className="flex items-center mt-1">
                  <Folder 
                    className="h-4 w-4 mr-1" 
                    style={{ color: targetFolder.color }}
                  />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {targetFolder.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search and Stats */}
        <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
          <div className="flex items-center justify-between mb-4">
            <div className="relative flex-1 max-w-md"> {/* Keep this div */}
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search prompts..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-border rounded-lg 
                  focus:outline-none focus:ring-2 focus:ring-fire-500 focus:border-transparent 
                  bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
              />
            </div>
            
            <div className="flex items-center space-x-3 ml-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {availablePrompts.length} available • {selectedPromptIds.size} selected
              </span>
              
              {availablePrompts.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleSelectAll}
                    disabled={selectedPromptIds.size === availablePrompts.length} // Keep this logic
                    className="text-xs text-fire-600 dark:text-fire-400 hover:text-fire-700 
                      dark:hover:text-fire-300 transition-colors disabled:opacity-50 
                      disabled:cursor-not-allowed underline"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleDeselectAll}
                    disabled={selectedPromptIds.size === 0} // Keep this logic
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-700 
                      dark:hover:text-gray-300 transition-colors disabled:opacity-50 
                      disabled:cursor-not-allowed underline"
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Prompts List */}
        <div className="flex-1 overflow-y-auto max-h-[400px]">
          {availablePrompts.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2">
                {searchQuery ? 'No matching prompts' : 'All prompts are already in folders'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery 
                  ? 'Try adjusting your search terms to find prompts to add.'
                  : 'All your prompts are already organized in folders.'}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {availablePrompts.map((prompt) => (
                <div
                  key={prompt.id}
                  onClick={() => handleTogglePrompt(prompt.id)}
                  className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-sm ${
                    selectedPromptIds.has(prompt.id) // Keep this logic
                      ? 'border-fire-300 dark:border-fire-600 bg-fire-50 dark:bg-fire-900/20'
                      : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface hover:border-fire-200 dark:hover:border-fire-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-2">
                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mr-3 transition-colors ${ // Keep this logic
                          selectedPromptIds.has(prompt.id)
                            ? 'border-fire-500 bg-fire-500'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {selectedPromptIds.has(prompt.id) && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-dark-text line-clamp-1">
                            {prompt.title}
                          </h4>
                          <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span>{formatDate(prompt.updatedAt)}</span>
                            {prompt.aiPlatform && (
                              <span className="flex items-center">
                                • {prompt.aiPlatform}
                              </span>
                            )}
                            {prompt.isTemplate && (
                              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded text-xs">
                                Template
                              </span>
                            )}
                            {prompt.isFavorite && (
                              <span className="text-yellow-500">★</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 ml-8">
                        {prompt.body}
                      </p>
                      
                      {/* Current location */}
                      <div className="flex items-center mt-2 ml-8 text-xs text-gray-500 dark:text-gray-400">
                        <span>Currently in: </span>
                        {prompt.folderId ? (
                          <span className="ml-1 font-medium">
                            {folders.find(f => f.id === prompt.folderId)?.name || 'Unknown folder'}
                          </span>
                        ) : (
                          <span className="ml-1 font-medium">Root folder</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          <button
            onClick={handleAddPrompts} // Keep this action
            disabled={selectedPromptIds.size === 0 || isLoading}
            className="bg-fire-gradient text-white px-6 py-2 rounded-lg hover:shadow-fire-glow 
              focus:outline-none focus:ring-2 focus:ring-fire-500 focus:ring-offset-2 
              transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
              shadow-fire-card transform hover:scale-105 active:scale-95 
              flex items-center font-semibold"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Adding...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4 mr-2" />
                Add {selectedPromptIds.size} Prompt{selectedPromptIds.size !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};